<template>
  <div class="ant-variant-selector" :class="{ 'ant-variant-selector--disabled': disabled }">
    <div
      v-for="group in normalizedGroups"
      :key="group.key"
      class="ant-variant-selector__group"
    >
      <div v-if="group.label" class="ant-variant-selector__label">
        {{ group.label }}
        <span v-if="selectedLabels[group.key]" class="ant-variant-selector__value">
          : {{ selectedLabels[group.key] }}
        </span>
      </div>

      <div class="ant-variant-selector__options">
        <button
          v-for="opt in group.options"
          :key="String(opt.value)"
          type="button"
          class="ant-variant-selector__option"
          :class="{
            'is-active': String(innerValue[group.key]) === String(opt.value),
            'is-disabled': opt.disabled || disabled,
            'is-color': !!opt.color,
          }"
          :disabled="opt.disabled || disabled"
          :title="opt.label"
          :style="opt.color ? { '--variant-color': opt.color } : undefined"
          @click="selectOption(group.key, opt)"
        >
          <span
            v-if="opt.color"
            class="ant-variant-selector__swatch"
            :style="{ background: opt.color }"
          />
          <span v-else>{{ opt.label }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { VariantGroup, VariantOption } from "./types";

type VariantValue = Record<string, string | number | null | undefined>;

interface Props {
  modelValue?: VariantValue;
  /** Nhóm biến thể: size, color, material... */
  groups?: VariantGroup[];
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}),
  groups: () => [],
  disabled: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: VariantValue): void;
  (e: "change", value: VariantValue, meta: { key: string; option: VariantOption }): void;
}>();

const normalizedGroups = computed(() => props.groups || []);

const innerValue = computed({
  get: () => props.modelValue || {},
  set: (val: VariantValue) => emit("update:modelValue", val),
});

const selectedLabels = computed(() => {
  const result: Record<string, string> = {};
  for (const group of normalizedGroups.value) {
    const selected = group.options.find(
      (opt) => String(opt.value) === String(innerValue.value[group.key])
    );
    if (selected) result[group.key] = selected.label;
  }
  return result;
});

const selectOption = (key: string, option: VariantOption) => {
  if (props.disabled || option.disabled) return;
  const next = { ...innerValue.value, [key]: option.value };
  emit("update:modelValue", next);
  emit("change", next, { key, option });
};
</script>

<style scoped>
.ant-variant-selector {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ant-variant-selector__label {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #555;
}

.ant-variant-selector__value {
  font-weight: 500;
  color: #111;
}

.ant-variant-selector__options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ant-variant-selector__option {
  min-width: 40px;
  min-height: 36px;
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #fff;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ant-variant-selector__option.is-color {
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 3px;
  border-radius: 50%;
}

.ant-variant-selector__swatch {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.ant-variant-selector__option:hover:not(.is-disabled) {
  border-color: #b19663;
}

.ant-variant-selector__option.is-active {
  border-color: #b19663;
  color: #b19663;
  box-shadow: 0 0 0 1px #b19663;
}

.ant-variant-selector__option.is-disabled {
  opacity: 0.4;
  cursor: not-allowed;
  text-decoration: line-through;
}

.ant-variant-selector--disabled {
  opacity: 0.7;
  pointer-events: none;
}
</style>

<!--
  Hướng dẫn sử dụng — Product Variant Selector
  Global: <h-variant v-model="variant" :groups="groups" />
  Import: import { ProductVariantSelector } from '@/components/ant/product'

  groups: [{ key: 'size', label: 'Size', options: [{ label: 'M', value: 'M', color?: '#000' }] }]
  v-model: { size: 'M', color: 'black' }
  Events: update:modelValue, change(value, { key, option })
  Docs: src/components/ant/product/README.md
-->
